(() => {
  class FoxtrotProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form = this.querySelector('.fg-purchase-form');
      this.master = this.querySelector('[data-fg-variant]');
      this.options = Array.from(this.querySelectorAll('[data-fg-option]'));
      this.quantity = this.querySelector('[data-fg-quantity]');
      this.plan = this.querySelector('[data-fg-selling-plan]');
      this.add = this.querySelector('[data-fg-add]');
      this.error = this.querySelector('[data-fg-product-error]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-fg-variants]').textContent);
      } catch (error) {
        console.error('Foxtrot product variant data could not be read. The standard variant selector remains available.', error);
        return;
      }
      if (!this.form || !this.master || !Array.isArray(this.variants) || !this.variants.length) {
        console.error('Foxtrot product is missing its form or variant data; purchase enhancement was not initialized.', this);
        return;
      }
      this.querySelector('[data-fg-options]')?.removeAttribute('hidden');
      const masterWrapper = this.querySelector('[data-fg-master-wrapper]');
      if (masterWrapper && this.querySelector('[data-fg-options]')) masterWrapper.hidden = true;
      this.querySelectorAll('[data-fg-quantity-step]').forEach((button) => { button.hidden = false; });
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-fg-option]')) this.update(this.findVariant(), true);
        if (event.target === this.master) {
          const variant = this.variants.find((item) => String(item.id) === this.master.value);
          this.setOptions(variant);
          this.update(variant, true);
        }
        if (event.target === this.plan) this.update(this.variant, false);
      }, { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-fg-quantity-step]');
        if (!button || !this.quantity) return;
        const direction = Number(button.dataset.fgQuantityStep);
        direction > 0 ? this.quantity.stepUp() : this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
      this.form.addEventListener('submit', (event) => {
        const validPlan = !this.plan || !this.plan.value || this.variant?.allocations.some((plan) => String(plan.id) === this.plan.value);
        if (!this.variant?.available || !validPlan || !this.master.value || (this.quantity && !this.quantity.checkValidity())) {
          event.preventDefault();
          this.showError(this.variant?.available ? this.dataset.invalidQuantity : this.dataset.unavailable);
          this.quantity?.reportValidity();
        }
      }, { signal });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const variant = this.variants.find((item) => String(item.id) === id) || this.variants.find((item) => item.available) || this.variants[0];
        this.setOptions(variant);
        this.update(variant, false);
      }, { signal });
      this.update(this.variants.find((item) => String(item.id) === this.master.value), false);
    }

    disconnectedCallback() { this.abort?.abort(); }

    findVariant() {
      return this.variants.find((variant) => variant.options.every((value, index) => value === this.options[index]?.value));
    }

    setOptions(variant) {
      if (variant) this.options.forEach((option, index) => { option.value = variant.options[index]; });
    }

    annotateOptions() {
      this.options.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          const matches = this.variants.filter((variant) => variant.options[index] === option.value &&
            this.options.every((other, otherIndex) => otherIndex === index || variant.options[otherIndex] === other.value));
          const available = matches.some((variant) => variant.available);
          const suffix = available ? '' : ` - ${matches.length ? this.dataset.soldOut : this.dataset.unavailableOption}`;
          option.textContent = option.value + suffix;
          // Keep values selectable so changing one option never traps the shopper.
          option.dataset.available = String(available);
        });
      });
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    update(variant, updateUrl) {
      this.variant = variant;
      this.showError('');
      this.master.value = variant ? String(variant.id) : '';
      this.master.disabled = !variant?.available;
      const specifications = this.querySelector('[data-fg-packability-display]');
      if (specifications) {
        const template = variant ? this.querySelector(`[data-fg-variant-specs="${variant.id}"]`) : null;
        specifications.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      }
      const classificationData = this.querySelector('[data-fg-classified-product]');
      if (classificationData && (variant?.equipment || !variant)) {
        const current = JSON.parse(classificationData.textContent);
        const equipment = variant?.equipment || { fgId: null, gearDefinitionId: null, packability: {}, tenCs: [], capabilities: [], redundancy: [] };
        classificationData.textContent = JSON.stringify({ ...current, ...equipment, variantId: variant?.id || null });
      }
      this.dispatchEvent?.(new Event('foxtrot:variant:changed'));
      this.annotateOptions();
      if (this.plan) {
        Array.from(this.plan.options).forEach((option) => {
          option.disabled = Boolean(option.value) && !variant?.allocations.some((plan) => String(plan.id) === option.value);
        });
        if (this.plan.selectedOptions[0]?.disabled) {
          this.plan.value = Array.from(this.plan.options).find((option) => !option.disabled)?.value || '';
        }
      }
      const allocation = variant?.allocations.find((plan) => String(plan.id) === this.plan?.value);
      const validPlan = !this.plan?.required || Boolean(allocation);
      const available = Boolean(variant?.available && validPlan);
      const label = !variant ? this.dataset.unavailable : available ? this.dataset.add : this.dataset.soldOut;
      if (this.add) this.add.disabled = !available;
      const buttonLabel = this.querySelector('[data-fg-add-label]');
      if (buttonLabel) buttonLabel.textContent = label;
      const payment = this.querySelector('[data-fg-payment]');
      if (payment) payment.hidden = !available;
      const status = this.querySelector('[data-fg-availability]');
      if (status) {
        status.textContent = !variant ? this.dataset.unavailable : variant.inventoryText || (variant.available ? this.dataset.available : this.dataset.soldOut);
        status.toggleAttribute('data-unavailable', !available);
      }
      const price = this.querySelector('[data-fg-price]');
      const compare = this.querySelector('[data-fg-compare]');
      const unit = this.querySelector('[data-fg-unit]');
      if (price) price.textContent = variant ? (allocation || variant).priceText : this.dataset.unavailable;
      if (compare) {
        compare.textContent = variant ? (allocation || variant).compareText : '';
        compare.hidden = !compare.textContent;
      }
      if (unit) {
        unit.textContent = allocation ? '' : variant?.unitText || '';
        unit.hidden = !unit.textContent;
      }
      if (this.quantity && variant) {
        const maxValues = [variant.max, variant.ruleMax].filter((value) => Number.isFinite(value));
        this.quantity.min = String(variant.min);
        this.quantity.step = String(variant.step);
        if (maxValues.length) this.quantity.max = String(Math.min(...maxValues));
        else this.quantity.removeAttribute('max');
        if (Number(this.quantity.value) < variant.min) this.quantity.value = String(variant.min);
      }
      if (variant?.media) this.querySelector('foxtrot-gallery')?.show(String(variant.media));
      const details = document.querySelector('.fg-product-details');
      if (details) {
        const sku = details.querySelector('[data-fg-spec-sku]');
        if (sku) sku.textContent = variant?.sku || '';
        const skuRow = details.querySelector('[data-fg-spec-sku-row]');
        if (skuRow) skuRow.hidden = !variant?.sku;
        details.querySelectorAll('[data-fg-spec-option]').forEach((value) => {
          value.textContent = variant?.options[Number(value.dataset.fgSpecOption)] || this.dataset.unavailable;
        });
      }
      if (updateUrl) {
        const url = new URL(location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
    }
  }

  class FoxtrotGallery extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.slides = Array.from(this.querySelectorAll('[data-fg-media]'));
      if (!this.slides.length) return;
      this.querySelector('[data-fg-gallery-controls]')?.removeAttribute('hidden');
      if (typeof this.querySelector('[data-fg-zoom-dialog]')?.showModal === 'function') {
        this.querySelectorAll('[data-fg-zoom-src]').forEach((button) => button.removeAttribute('hidden'));
      }
      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-fg-zoom-src]');
        if (zoom) {
          const dialog = this.querySelector('[data-fg-zoom-dialog]');
          const image = dialog?.querySelector('[data-fg-zoom-image]');
          if (!dialog || !image || typeof dialog.showModal !== 'function') {
            console.error('Foxtrot image zoom is unavailable in this browser.');
            return;
          }
          image.src = zoom.dataset.fgZoomSrc;
          image.alt = zoom.dataset.fgZoomAlt || '';
          this.zoomTrigger = zoom;
          dialog.showModal();
          return;
        }
        const thumb = event.target.closest('[data-fg-thumbnail]');
        if (thumb) {
          event.preventDefault();
          this.show(thumb.dataset.fgThumbnail);
        }
        if (event.target.closest('[data-fg-gallery-prev]')) this.move(-1);
        if (event.target.closest('[data-fg-gallery-next]')) this.move(1);
      }, { signal });
      const zoomDialog = this.querySelector('[data-fg-zoom-dialog]');
      zoomDialog?.addEventListener('close', () => {
        this.zoomTrigger?.focus();
        this.zoomTrigger = null;
      }, { signal });
      zoomDialog?.addEventListener('click', (event) => {
        if (event.target === zoomDialog) zoomDialog.close();
      }, { signal });
      const stage = this.querySelector('[data-fg-stage]');
      stage.addEventListener('keydown', (event) => {
        if (event.target !== stage || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.show(this.slides[0].dataset.fgMedia);
        else if (event.key === 'End') this.show(this.slides.at(-1).dataset.fgMedia);
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      stage.addEventListener('pointerdown', (event) => {
        this.touch = event.pointerType === 'touch' && event.target.tagName === 'IMG' ? { x: event.clientX, y: event.clientY } : null;
      }, { signal });
      stage.addEventListener('pointerup', (event) => {
        if (!this.touch) return;
        const x = event.clientX - this.touch.x;
        const y = event.clientY - this.touch.y;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y)) this.move(x < 0 ? 1 : -1);
        this.touch = null;
      }, { signal });
      stage.addEventListener('pointercancel', () => { this.touch = null; }, { signal });
      this.show((this.slides.find((slide) => !slide.hidden) || this.slides[0]).dataset.fgMedia);
    }

    disconnectedCallback() { this.abort?.abort(); }

    move(direction) {
      const index = this.slides.findIndex((slide) => !slide.hidden);
      this.show(this.slides[(index + direction + this.slides.length) % this.slides.length].dataset.fgMedia);
    }

    show(id) {
      const selected = this.slides?.find((slide) => slide.dataset.fgMedia === id);
      if (!selected) return;
      this.slides.forEach((slide) => {
        slide.hidden = slide !== selected;
        if (slide.hidden) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          // Removing external frames stops playback without cross-origin player APIs.
          if (slide.querySelector('template')) slide.querySelectorAll('iframe').forEach((frame) => frame.remove());
          slide.querySelector('model-viewer')?.pause?.();
        }
      });
      const template = selected.querySelector('template');
      if (template && !selected.querySelector('video, iframe, model-viewer')) {
        selected.append(template.content.cloneNode(true));
        const model = selected.querySelector('model-viewer');
        if (model) {
          if (window.Shopify?.loadFeatures) {
            window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
              if (errors) console.error('Foxtrot 3D viewer could not load.', errors);
              else new window.Shopify.ModelViewerUI(model);
            } }]);
          } else console.error('Shopify model viewer loader is unavailable.');
        }
      }
      this.querySelectorAll('[data-fg-thumbnail]').forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.fgThumbnail === id));
      });
      const count = this.querySelector('[data-fg-gallery-count]');
      if (count) count.textContent = `${this.slides.indexOf(selected) + 1} / ${this.slides.length}`;
      const thumb = this.querySelector(`[data-fg-thumbnail="${id}"]`);
      if (thumb) {
        const rail = thumb.parentElement;
        const vertical = getComputedStyle(rail).flexDirection === 'column';
        const position = vertical
          ? thumb.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop
          : thumb.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
        const start = vertical ? rail.scrollTop : rail.scrollLeft;
        const size = vertical ? thumb.offsetHeight : thumb.offsetWidth;
        const viewport = vertical ? rail.clientHeight : rail.clientWidth;
        if (position < start || position + size > start + viewport) {
          rail.scrollTo(vertical ? { top: position, behavior: 'auto' } : { left: position, behavior: 'auto' });
        }
      }
    }
  }

  class FoxtrotRecommendations extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      if (this.querySelector('[data-fg-recommendations-content]')) return;
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.load();
        }
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      clearTimeout(this.timeout);
    }

    async load() {
      this.timeout = setTimeout(() => this.abort.abort(new Error('Recommendations timed out.')), 10000);
      try {
        const response = await fetch(this.dataset.url, { signal: this.abort.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Recommendations returned HTTP ${response.status}.`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = html.querySelector('foxtrot-recommendations');
        if (!root) throw new Error('Recommendations response is missing its section.');
        const content = root.querySelector('[data-fg-recommendations-content]');
        if (content && content.querySelector('.fg-product-card')) this.prepend(content);
        else this.hidden = true;
      } catch (error) {
        if (!this.isConnected) return;
        console.error('Foxtrot recommendations could not be loaded.', error);
        const message = this.querySelector('[data-fg-recommendations-error]');
        message.textContent = this.dataset.error;
        message.hidden = false;
      } finally {
        clearTimeout(this.timeout);
      }
    }
  }

  function initializeRecentlyViewed() {
    const root = document.querySelector('[data-fg-recently-viewed]');
    if (!root) return;
    const list = root.querySelector('[data-fg-recently-viewed-list]');
    const empty = root.querySelector('[data-fg-recently-viewed-empty]');
    const errorMessage = root.querySelector('[data-fg-recently-viewed-error]');
    const showHistoryError = (message, error) => {
      console.error(message, error);
      if (errorMessage) {
        errorMessage.textContent = errorMessage.dataset.errorText;
        errorMessage.hidden = false;
        root.hidden = false;
      }
    };
    const storageKey = 'foxtrot:recently-viewed:v1';
    let historyItems;
    try {
      const stored = sessionStorage.getItem(storageKey);
      historyItems = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(historyItems) || historyItems.some((item) => !item || typeof item.id !== 'string'
        || typeof item.title !== 'string' || typeof item.url !== 'string')) {
        throw new Error('The recently viewed session history is invalid.');
      }
    } catch (error) {
      showHistoryError('Foxtrot could not read recently viewed products.', error);
      return;
    }

    const current = {
      id: root.dataset.productId,
      title: root.dataset.productTitle,
      url: root.dataset.productUrl,
      image: root.dataset.productImage
    };
    if (!current.id || !current.title || !current.url) {
      showHistoryError('Foxtrot recently viewed is missing current product data.', new Error('Current product data is incomplete.'));
      return;
    }
    historyItems = [current, ...historyItems.filter((item) => item.id !== current.id)].slice(0, 6);
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(historyItems));
    } catch (error) {
      showHistoryError('Foxtrot could not save recently viewed products for this tab session.', error);
      return;
    }

    const priorItems = historyItems.slice(1);
    list.replaceChildren();
    priorItems.forEach((item) => {
      let url;
      try {
        url = new URL(item.url, location.origin);
      } catch (error) {
        console.error('Foxtrot skipped an invalid recently viewed product URL.', error);
        return;
      }
      if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) {
        console.error('Foxtrot skipped an invalid recently viewed product URL.');
        return;
      }
      const row = document.createElement('li');
      const link = document.createElement('a');
      link.href = `${url.pathname}${url.search}${url.hash}`;
      if (typeof item.image === 'string' && /^https:\/\/cdn\.shopify\.com\//.test(item.image)) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        link.append(image);
      }
      const title = document.createElement('span');
      title.textContent = item.title;
      link.append(title);
      row.append(link);
      list.append(row);
    });
    if (list.children.length) root.hidden = false;
    else if (empty) empty.hidden = false;
  }

  initializeRecentlyViewed();

  for (const [name, type] of [
    ['foxtrot-gallery', FoxtrotGallery], ['foxtrot-product', FoxtrotProduct], ['foxtrot-recommendations', FoxtrotRecommendations]
  ]) {
    if (!customElements.get(name)) customElements.define(name, type);
  }
})();
